import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import LanguageSwitcher from './LanguageSwitcher';
import { Button } from './ui/button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getCartCount, setIsCartOpen } = useCart();
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-opacity hover:opacity-80">
            <img 
              src="/logo-header.png" 
              alt="Eman's Peppers" 
              width="64"
              height="64"
              loading="eager"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive(link.path) 
                    ? 'text-[#D4A373]' 
                    : 'text-[#F7F3EE] hover:text-[#D4A373]'
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/bulk"
              className="px-4 py-2 rounded-full text-xs font-bold tracking-[0.15em] uppercase transition-transform hover:scale-[1.04] shadow-md"
              style={{
                backgroundColor: '#D4A373',
                color: '#0C0A09',
                fontFamily: 'Manrope, sans-serif',
                boxShadow: '0 0 18px rgba(212, 163, 115, 0.35)',
              }}
              data-testid="nav-bulk-buy-desktop"
            >
              Bulk Buy
            </Link>
          </nav>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <LanguageSwitcher />

            <Button
              variant="ghost"
              size="icon"
              className="relative hover:bg-[#1A1513] transition-colors"
              onClick={() => setIsCartOpen(true)}
              style={{ color: '#F7F3EE' }}
            >
              <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D4A373] text-[#0C0A09] text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                  {getCartCount()}
                </span>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden hover:bg-[#1A1513] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: '#F7F3EE' }}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-[#362B25]">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-sm font-medium transition-colors ${
                  isActive(link.path) 
                    ? 'text-[#D4A373]' 
                    : 'text-[#F7F3EE] hover:text-[#D4A373]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/bulk"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-3 block text-center py-3 rounded-full text-sm font-bold tracking-[0.15em] uppercase"
              style={{
                backgroundColor: '#D4A373',
                color: '#0C0A09',
                fontFamily: 'Manrope, sans-serif',
              }}
              data-testid="nav-bulk-buy-mobile"
            >
              Bulk Buy
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
